/**
 * Admin CMS API. Mirrors the /cms/* routes in engine src/routes/admin.js, every one of
 * which is behind requireAdmin.
 *
 * ── THE CLIENT DESCRIBES, IT DOES NOT DECIDE ─────────────────────────────────────────
 *
 * Nothing here validates a document, chooses a scope, or works out which template an
 * event resolves through. Those answers belong to the engine, which owns the data and is
 * the only place a decision can be enforced. This file is the shape of the conversation
 * and nothing more — a second opinion computed in the browser would eventually disagree
 * with the server, and the browser's copy would be the wrong one.
 */

import { api } from './apiClient';
import type { BlockDocument } from '../cms/BlockRenderer';

export type CmsScope = 'platform' | 'organization';
export type EntityKind = 'event' | 'venue' | 'artist' | 'standalone';

/**
 * What a document is validated and rendered against.
 *
 * The four entity kinds, plus `global_section` — which is NOT an entity. It is the
 * inside of a reusable section, and the distinction is what stops an EventTickets block
 * being placed in a footer that renders on pages with no event at all.
 */
export type BlockContext = EntityKind | 'global_section';
export type PageKind = 'standalone' | 'entity_override';
export type VersionState = 'draft' | 'published' | 'superseded' | 'archived';

export interface CmsSeo {
  title: string | null;
  description: string | null;
  ogImageAssetId: string | null;
}

/** One block, as the registry describes it. The editor's inspector is generated from this. */
export interface BlockManifest {
  id: string;
  label: string;
  version: number;
  category: string;
  contexts: EntityKind[];
  children: 'any' | 'none';
  critical: boolean;
  dataRequirements: string[];
  defaultProps: Record<string, unknown>;
  propsSchema?: {
    type?: string;
    properties?: Record<string, PropSchema>;
    additionalProperties?: boolean;
  };
  editor?: BlockEditorHints;
}

/**
 * How the inspector should present this block.
 *
 * `copyKeys` is the seam to the Copy Layer: a block whose words come from Copy declares
 * them here so the inspector can show what the page will actually say and link to where
 * it is changed, instead of offering a second, competing place to edit the same sentence.
 */
export interface BlockEditorHints {
  fields?: Record<string, EditorField>;
  copyKeys?: { key: string; label: string }[];
  note?: string;
}

export interface PropSchema {
  type?: string | string[];
  enum?: unknown[];
  format?: string;
  nullable?: boolean;
  maxLength?: number;
  /** Set by the manifest when this prop holds a media asset id. */
  mediaReference?: boolean;
  mediaKind?: 'image' | 'video';
  /** Set when this prop holds a global section id — a live reference, not a document. */
  globalSectionReference?: boolean;
  maxItems?: number;
}

export interface EditorField {
  label?: string;
  control?: 'text' | 'textarea' | 'select' | 'boolean' | 'media' | 'number'
    | 'globalSection' | 'links' | 'logos';
  helpText?: string;
  placeholder?: string;
  /** Which kind of asset the media picker should offer. Mirrors the prop schema. */
  mediaKind?: 'image' | 'video';
  /** A display heading, not an id — the inspector groups fields by this string. */
  group?: string;
}

export interface CmsTemplate {
  id: string;
  scope: CmsScope;
  organizationId: string | null;
  entityKind: EntityKind;
  name: string;
  description: string | null;
  isDefault: boolean;
  status: string;
  createdAt: string;
  /** The theme this template assigns. NULL means inherit. */
  themeId: string | null;
  publishedVersion: number | null;
  draftVersion: number | null;
  /** Events pointed at this template by an explicit assignment. */
  assignedEntities?: number;
}

export interface CmsPage {
  id: string;
  pageKind: PageKind;
  scope: CmsScope;
  organizationId: string | null;
  slug: string | null;
  entityKind: EntityKind | null;
  entityId: string | null;
  name: string;
  status: string;
  createdAt: string;
  /** The theme this page assigns. NULL means inherit from the template. */
  themeId: string | null;
  publishedVersion: number | null;
  draftVersion: number | null;
}

export interface CmsVersion {
  id: string;
  version: number;
  state: VersionState;
  document: BlockDocument;
  seo: CmsSeo | null;
  createdAt: string;
  createdBy?: string;
  publishedAt: string | null;
}

/** What publishing this template would change. */
export interface TemplateUsage {
  templateId: string;
  assigned: number;
  viaDefault: number;
  isDefault: boolean;
  scope: CmsScope;
  visibleEvents: number;
}

/** Would Publish succeed? Asked BEFORE the button is pressed. */
export type PublishReadiness =
  | { ready: true; counts: Record<string, number> }
  | {
    ready: false;
    reason: string;
    message?: string;
    component?: string;
    nodeId?: string;
  };

export interface ResolutionStep {
  step: 'page_override' | 'assigned_template' | 'organization_default'
    | 'platform_default' | 'hardcoded_fallback';
  exists: boolean;
  published: boolean;
  name?: string;
}

export interface EventResolution {
  eventId: string;
  organizationId: string | null;
  assignedTemplateId: string | null;
  resolvedFrom: ResolutionStep['step'];
  version: number | null;
  candidates: ResolutionStep[];
}

export type EventPreview =
  | { resolved: false }
  | {
    resolved: true;
    source: ResolutionStep['step'];
    version: number;
    /** True when what you are looking at is an unpublished draft. */
    draft: boolean;
    document: BlockDocument;
    seo: CmsSeo | null;
  };

const q = (params: Record<string, string | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const out = s.toString();
  return out ? `?${out}` : '';
};

// ── Registry ──
export const listBlocks = () => api.get<BlockManifest[]>('/admin/cms/blocks');

// ── Templates ──
export const listTemplates = (filter: {
  organizationId?: string | null; entityKind?: EntityKind | null;
} = {}) => api.get<CmsTemplate[]>(`/admin/cms/templates${q(filter)}`);

export const getTemplate = (id: string) =>
  api.get<CmsTemplate>(`/admin/cms/templates/${id}`);

export const createTemplate = (body: {
  scope: CmsScope; organizationId?: string | null; entityKind: EntityKind;
  name: string; description?: string | null; isDefault?: boolean;
}) => api.post<CmsTemplate>('/admin/cms/templates', body);

export const setTemplateDefault = (id: string, isDefault: boolean) =>
  api.patch<CmsTemplate>(`/admin/cms/templates/${id}/default`, { isDefault });

export const templateUsage = (id: string) =>
  api.get<TemplateUsage>(`/admin/cms/templates/${id}/usage`);

export const templateReadiness = (id: string) =>
  api.get<PublishReadiness>(`/admin/cms/templates/${id}/readiness`);

export const archiveTemplate = (id: string, replacementTemplateId: string | null = null) =>
  api.post<{ archived: true }>(`/admin/cms/templates/${id}/archive`, { replacementTemplateId });

// ── Pages ──
export const listPages = (filter: {
  organizationId?: string | null; pageKind?: PageKind | null;
} = {}) => api.get<CmsPage[]>(`/admin/cms/pages${q(filter)}`);

export const getPage = (id: string) => api.get<CmsPage>(`/admin/cms/pages/${id}`);

export const createPage = (body: {
  pageKind: PageKind; scope?: CmsScope; organizationId?: string | null;
  slug?: string | null; entityKind?: EntityKind | null; entityId?: string | null;
  name: string;
}) => api.post<CmsPage>('/admin/cms/pages', body);

export const pageReadiness = (id: string) =>
  api.get<PublishReadiness>(`/admin/cms/pages/${id}/readiness`);

export const archivePage = (id: string) =>
  api.post<{ archived: true }>(`/admin/cms/pages/${id}/archive`, {});

// ── Versions ──
//
// A template and a page are edited through the SAME three calls. Which one is being
// edited is a parameter, not a different code path, because a divergence between the two
// is how an editor ends up able to do something to a template it cannot do to a page.

export type OwnerKind = 'template' | 'page' | 'global_section';
export type OwnerRef = { kind: OwnerKind; id: string };

const OWNER_PATH: Record<OwnerKind, string> = {
  template: 'templates',
  page: 'pages',
  global_section: 'global-sections',
};

const ownerPath = (owner: OwnerRef) => `/admin/cms/${OWNER_PATH[owner.kind]}/${owner.id}`;

export const listVersions = (owner: OwnerRef) =>
  api.get<CmsVersion[]>(`${ownerPath(owner)}/versions`);

/**
 * Save the draft.
 *
 * `expectedVersion` is the draft version this editor loaded. The engine refuses the write
 * when somebody else has saved since — losing a colleague's work silently is worse than
 * being told to reload.
 */
export const saveDraft = (owner: OwnerRef, body: {
  document: BlockDocument; seo?: CmsSeo | null; expectedVersion: number | null;
}) => api.put<CmsVersion>(`${ownerPath(owner)}/draft`, body);

export const publishVersion = (owner: OwnerRef, expectedVersion: number | null) =>
  api.post<CmsVersion>(`${ownerPath(owner)}/publish`, { expectedVersion });

export const readiness = (owner: OwnerRef) =>
  api.get<PublishReadiness>(`${ownerPath(owner)}/readiness`);

// ── Events ──
export const eventResolution = (eventId: string) =>
  api.get<EventResolution>(`/admin/cms/events/${eventId}/resolution`);

/** Resolves INCLUDING drafts. A separate endpoint from the public resolver, on purpose. */
export const eventPreview = (eventId: string) =>
  api.get<EventPreview>(`/admin/cms/events/${eventId}/preview`);

/** `templateId: null` clears the assignment and returns the event to the default chain. */
export const assignEventTemplate = (eventId: string, templateId: string | null) =>
  api.patch<{ eventId: string; templateId: string | null }>(
    `/admin/cms/events/${eventId}/template`, { templateId });

export const createEventOverride = (eventId: string, body: {
  name?: string | null; startFrom?: 'resolved' | 'blank';
}) => api.post<CmsPage>(`/admin/cms/events/${eventId}/override`, body);

// ── Global sections (task 13) ──
//
// Reusable content, referenced live. A template names one; it never contains one, so
// nothing here returns a section's document to be embedded anywhere.

export type SectionType = 'footer' | 'announcement' | 'sponsor_strip';

export interface GlobalSection {
  id: string;
  sectionType: SectionType;
  scope: CmsScope;
  organizationId: string | null;
  organizationName?: string;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  publishedVersion: number | null;
  draftVersion: number | null;
  /** Versions that mention it, published or draft. A cheap list-screen figure. */
  referencedBy?: number;
}

/** What publishing this section would change. */
export interface SectionUsage {
  sectionId: string;
  templates: number;
  pages: number;
  drafts: number;
  events: number;
  visibleEvents: number;
}

export const listGlobalSections = (filter: {
  organizationId?: string | null; sectionType?: SectionType | null;
} = {}) => api.get<GlobalSection[]>(`/admin/cms/global-sections${q(filter)}`);

export const listSectionTypes = () =>
  api.get<SectionType[]>('/admin/cms/global-section-types');

export const getGlobalSection = (id: string) =>
  api.get<GlobalSection>(`/admin/cms/global-sections/${id}`);

export const createGlobalSection = (body: {
  sectionType: SectionType; scope: CmsScope; organizationId?: string | null;
  name: string; description?: string | null;
}) => api.post<GlobalSection>('/admin/cms/global-sections', body);

export const globalSectionUsage = (id: string) =>
  api.get<SectionUsage>(`/admin/cms/global-sections/${id}/usage`);

export const archiveGlobalSection = (id: string) =>
  api.post<{ archived: true }>(`/admin/cms/global-sections/${id}/archive`, {});

// ── Themes (task 13) ──
//
// A theme is TOKEN DATA. There is no field here that accepts CSS, a selector or a font
// URL, and `ThemeTokenSpec` below is the complete list of what a theme may say.

export interface CmsTheme {
  id: string;
  scope: CmsScope;
  organizationId: string | null;
  name: string;
  description: string | null;
  isDefault: boolean;
  status: string;
  createdAt: string;
  publishedVersion: number | null;
  draftVersion: number | null;
}

export interface ThemeVersion {
  id: string;
  version: number;
  state: VersionState;
  tokens: Record<string, string>;
  createdAt: string;
  createdBy?: string;
  publishedAt: string | null;
}

/** One token, as the engine describes it. The editor is generated from these. */
export interface ThemeTokenSpec {
  name: string;
  kind: 'color' | 'enum';
  group: string;
  label: string;
  help: string | null;
  /** For an enum token: the only values it may hold. */
  values: string[] | null;
  cssVar: string;
  platformDefault: string;
}

export interface ThemeUsage {
  themeId: string;
  scope: CmsScope;
  isDefault: boolean;
  templates: number;
  pages: number;
  organizations: number;
  events: number;
  visibleEvents: number;
}

export interface ThemeResolutionStep {
  step: 'page' | 'template' | 'venue' | 'organization' | 'platform';
  exists: boolean;
  published: boolean;
  themeId?: string;
  version?: number | null;
  /** The venue step is designed and deliberately not built. See docs/CMS.md. */
  notImplemented?: boolean;
}

export interface ResolvedThemeBundle {
  source: string;
  themeId: string | null;
  version: number | null;
  draft?: boolean;
  tokens: Record<string, string>;
  candidates: ThemeResolutionStep[];
}

export const listThemeTokens = () => api.get<ThemeTokenSpec[]>('/admin/cms/theme-tokens');

export const listThemes = (filter: { organizationId?: string | null } = {}) =>
  api.get<CmsTheme[]>(`/admin/cms/themes${q(filter)}`);

export const getTheme = (id: string) => api.get<CmsTheme>(`/admin/cms/themes/${id}`);

export const createTheme = (body: {
  scope: CmsScope; organizationId?: string | null; name: string;
  description?: string | null; isDefault?: boolean;
}) => api.post<CmsTheme>('/admin/cms/themes', body);

export const listThemeVersions = (id: string) =>
  api.get<ThemeVersion[]>(`/admin/cms/themes/${id}/versions`);

export const saveThemeDraft = (id: string, body: {
  tokens: Record<string, string>; expectedVersion: number | null;
}) => api.put<ThemeVersion>(`/admin/cms/themes/${id}/draft`, body);

export const publishTheme = (id: string, expectedVersion: number | null) =>
  api.post<ThemeVersion>(`/admin/cms/themes/${id}/publish`, { expectedVersion });

export const themeUsage = (id: string) => api.get<ThemeUsage>(`/admin/cms/themes/${id}/usage`);

export const setThemeDefault = (id: string, isDefault: boolean) =>
  api.patch<CmsTheme>(`/admin/cms/themes/${id}/default`, { isDefault });

export const archiveTheme = (id: string, replacementThemeId: string | null = null) =>
  api.post<{ archived: true }>(`/admin/cms/themes/${id}/archive`, { replacementThemeId });

/** Resolves INCLUDING the draft, for the editor's live preview. Admin-only. */
export const previewTheme = (id: string) =>
  api.get<ResolvedThemeBundle>(`/admin/cms/themes/${id}/preview`);

/** `themeId: null` clears the assignment and returns the owner to inheriting. */
export const assignTemplateTheme = (templateId: string, themeId: string | null) =>
  api.patch<{ themeId: string | null }>(`/admin/cms/templates/${templateId}/theme`, { themeId });

export const assignPageTheme = (pageId: string, themeId: string | null) =>
  api.patch<{ themeId: string | null }>(`/admin/cms/pages/${pageId}/theme`, { themeId });

export const setOrganizationTheme = (organizationId: string, themeId: string | null) =>
  api.patch<{ themeId: string | null }>(
    `/admin/cms/organizations/${organizationId}/theme`, { themeId });

/** Which theme this event resolves, and which steps were skipped. */
export const eventTheme = (eventId: string) =>
  api.get<ResolvedThemeBundle>(`/admin/cms/events/${eventId}/theme`);

// The editor treats a global section like any other owner: same draft, publish, version
// and readiness calls, reached through the same OwnerRef.
export type SectionOwnerRef = { kind: 'global_section'; id: string };
